/**
 * 안전 도구 (§5.30, v1.21) — 신고·차단·채팅 조정의 단일 원천.
 *
 * Play의 사용자 제작 콘텐츠(UGC) 정책은 다른 사용자에게 보이는 콘텐츠(쪽지·댓글·사진·가정 소식)를
 * 가진 앱에 신고·차단 수단과 조정(moderation)을 요구한다. 코드·라벨·문구·자동 검사를 여기 두고
 * API(검사·처리)와 화면(문구)이 같은 정의를 쓴다.
 */
export const REPORT_TARGET_TYPES = [
    'MESSAGE',
    'COMMENT',
    'PHOTO',
    'DAILY_NOTE',
    'HOME_MESSAGE',
    'USER',
];
export const REPORT_TARGET_LABELS = {
    MESSAGE: '쪽지',
    COMMENT: '댓글',
    PHOTO: '사진',
    DAILY_NOTE: '알림장',
    HOME_MESSAGE: '가정 소식',
    USER: '사용자',
};
export const REPORT_REASONS = [
    { code: 'HARASSMENT', label: '괴롭힘·모욕' },
    { code: 'INAPPROPRIATE', label: '부적절한 내용' },
    { code: 'PRIVACY', label: '개인정보 노출' },
    { code: 'CHILD_SAFETY', label: '아이 안전 우려' },
    { code: 'SPAM', label: '스팸·광고' },
    { code: 'OTHER', label: '기타' },
];
export const REPORT_REASON_CODES = REPORT_REASONS.map((r) => r.code);
export function reportReasonLabel(code) {
    return REPORT_REASONS.find((r) => r.code === code)?.label ?? '기타';
}
export const REPORT_STATUSES = ['OPEN', 'RESOLVED', 'DISMISSED'];
export const REPORT_STATUS_LABELS = {
    OPEN: '확인 대기',
    RESOLVED: '처리 완료',
    DISMISSED: '문제 없음',
};
/** 원장이 신고를 닫으며 한 일 */
export const REPORT_ACTIONS = ['HIDDEN', 'WARNED', 'NONE'];
export const REPORT_ACTION_LABELS = {
    HIDDEN: '내용 숨김',
    WARNED: '당사자에게 안내',
    NONE: '조치 없음',
};
export const REPORT_DETAIL_MAX = 500;
/** 한 사람이 하루에 보낼 수 있는 신고 수 — 오남용 방지 */
export const REPORT_DAILY_LIMIT = 20;
/** 화면 문구 — 학부모 노출 문자열은 여기서만 (§13.1-③) */
export const SAFETY_COPY = {
    menu: '신고·차단',
    report: '신고',
    reportTitle: '신고하기',
    reportReasonLabel: '어떤 문제인가요?',
    reportDetailLabel: '자세한 내용 (선택)',
    reportDetailPlaceholder: '원장님이 확인할 때 도움이 될 내용을 적어 주세요.',
    reportHint: '신고는 원장님과 새싹돋보기 운영진에게 전해져요. 신고한 사실은 상대에게 알리지 않아요.',
    reportSubmit: '신고 보내기',
    reportDone: '신고를 접수했어요. 원장님이 확인한 뒤 필요한 조치를 해요.',
    block: '차단',
    unblock: '차단 해제',
    blockTitle: '사용자 차단',
    blockConfirm: (name) => `${name}님을 차단할까요? 서로 쪽지를 주고받을 수 없고, 이분의 댓글과 소식이 내 화면에서 사라져요. 앱 설정에서 언제든 해제할 수 있어요.`,
    blockedBanner: '차단한 상대예요. 쪽지를 주고받을 수 없어요.',
    blockedByOther: '지금은 이 쪽지함에 보낼 수 없어요.',
    blocksTitle: '차단한 사용자',
    blocksEmpty: '차단한 사용자가 없어요.',
    blocksHint: '차단을 해제하면 다시 쪽지를 주고받고 댓글도 볼 수 있어요.',
    moderationAbuse: '서로를 존중하는 표현으로 바꿔 주세요. 이 내용은 보내지 않았어요.',
    moderationPrivacy: '주민등록번호나 카드번호로 보이는 숫자가 있어요. 개인정보 보호를 위해 보내지 않았어요.',
    hiddenContent: '원장님이 숨긴 내용이에요.',
    reportsTitle: '신고 접수',
    reportsEmpty: '접수된 신고가 없어요.',
    reportsHint: '신고를 확인하고 내용을 숨기거나 당사자에게 안내한 뒤 닫아 주세요. 처리 내역은 운영진도 봅니다.',
};
/**
 * 욕설·모욕 표현. 어린이집 대화에 흔한 일상어("꼭꼭 씹어요", "불이 꺼져요", "강아지 새끼",
 * "출시 발표")가 걸리지 않도록 뒤따르는 글자를 함께 본다. 목록은 보수적으로 유지한다 —
 * 사람을 향한 욕에만 반응하고, 애매하면 통과시켜 신고·원장 검토(사람의 조정)에 맡긴다.
 */
const ABUSE_PATTERNS = [
    /씨발|씨팔|시팔|ㅅㅂ|ㅆㅂ/,
    /시발(?![점전표])/,
    /병신|븅신|빙신|ㅂㅅ/,
    /개새끼|개새|새끼야|이새끼|저새끼|새끼들아|ㅅㄲ/,
    /미친 ?놈|미친 ?년|미친 ?새/,
    /좆|존나|ㅈㄴ/,
    /지랄/,
    /닥쳐/,
    /꺼져라|꺼져 ?버려|(^|\s)꺼져[!?.~]*(\s|$)/,
    /죽여 ?버|죽어 ?버려|죽어라|뒤져라/,
    /등신|또라이|돌아이|멍청이/,
    /쓰레기 ?같|쓰레기야/,
    /씹(?:할|새|년|놈|창)/,
    /니미|느금|엠창|호로새|호로자식|후레자식/,
    /창녀|걸레년|쌍놈|쌍년|썅/,
    /개 ?같은|개 ?같이|개소리/,
    /엿 ?먹/,
    /\b(fuck|shit|bitch|asshole)\b/i,
];
/** 주민등록번호 — 앞 6자리가 날짜 꼴이고 뒤 첫 자리가 1~4 */
const RRN_PATTERN = /(?<!\d)(\d{2})(\d{2})(\d{2})-?([1-4])\d{6}(?!\d)/g;
/** 카드번호 — 16자리 + Luhn 검사로 계좌번호·주문번호 오탐을 줄인다 */
const CARD_PATTERN = /(?<!\d)(?:\d{4}[- ]?){3}\d{4}(?!\d)/g;
function luhnValid(digits) {
    let sum = 0;
    let double = false;
    for (let i = digits.length - 1; i >= 0; i -= 1) {
        let n = Number(digits[i]);
        if (double) {
            n *= 2;
            if (n > 9)
                n -= 9;
        }
        sum += n;
        double = !double;
    }
    return sum % 10 === 0;
}
/** 욕설·모욕과 개인정보(주민등록번호·카드번호)를 자동으로 걸러 낸다 — 쪽지·댓글·가정 소식 */
export function moderateMessage(text) {
    const normalized = text.normalize('NFC');
    for (const match of normalized.matchAll(RRN_PATTERN)) {
        const month = Number(match[2]);
        const day = Number(match[3]);
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
            return { ok: false, reason: 'PRIVACY', matches: ['주민등록번호'] };
        }
    }
    for (const match of normalized.matchAll(CARD_PATTERN)) {
        if (luhnValid(match[0].replace(/\D/g, ''))) {
            return { ok: false, reason: 'PRIVACY', matches: ['카드번호'] };
        }
    }
    const matches = ABUSE_PATTERNS.flatMap((pattern) => {
        const found = normalized.match(pattern);
        return found ? [found[0].trim()] : [];
    }).filter(Boolean);
    if (matches.length > 0)
        return { ok: false, reason: 'ABUSE', matches };
    return { ok: true, reason: null, matches: [] };
}
export function moderationMessage(result) {
    return result.reason === 'PRIVACY' ? SAFETY_COPY.moderationPrivacy : SAFETY_COPY.moderationAbuse;
}
//# sourceMappingURL=safety.js.map